import {AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ContentChild, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {NgOption, NgSelectComponent} from '@ng-select/ng-select';
import {debounceTime, Subject} from 'rxjs';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {AddTagFn, CompareWithFn as NgSelectCompareWithFn, GroupValueFn as NgSelectGroupValueFn} from '@ng-select/ng-select/lib/ng-select.component';
import {I18nMessage} from '@shared/api/model/i18n';
import {CsvUtils} from '@shared/util/general/csv-utils';
import {CssVariablesService} from '@shared/services/util/css-variables.service';
import {HtmlUtils} from '@shared/util/general/html-utils';
import {MessageTranslateService} from '@shared/services';

export type CompareWithFn = NgSelectCompareWithFn;
export type GroupValueFn = NgSelectGroupValueFn;

export interface AceSelectOptionChild<V, G> extends Omit<NgOption, 'children'> {
  value: V;
  parent: AceSelectOptionGroup<V, G> | null;
}

export interface AceSelectOptionGroup<V, G> extends Omit<NgOption, 'parent'> {
  value: G;
  children: AceSelectOptionChild<V, G>[];
}

export enum ACE_SELECT_SIZE {
  SM = 'sm',
  MD = 'md'
}

/**
 * V = value
 * G = group
 */
export type AceSelectOption<V = any, G = any> = AceSelectOptionChild<V, G> | AceSelectOptionGroup<V, G>;

@Component({
  selector: 'app-ace-select',
  templateUrl: './ace-select.component.html',
  styleUrls: ['./ace-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AceSelectComponent), multi: true},
  ],
  standalone: false
})
export class AceSelectComponent implements OnInit, OnChanges, ControlValueAccessor, AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() items: any = [];
  @Input() placeholder: string;
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() groupBy: string | Function;
  @Input() multiSelect = false;
  @Input() loading = false;
  @Input() typeahead: Subject<string> | undefined;
  @Input() hideSelected = false;
  @Input() virtualScroll = false;
  @Input() selectableGroup: boolean;
  @Input() selectableGroupAsModel = false;
  @Input() compareWith: CompareWithFn | undefined;
  @Input() groupValue: GroupValueFn | undefined;
  @Input() searchFn: any;
  @Input() appendTo: string;
  @Input() addTag: boolean | AddTagFn;
  @Input() disableArrow = false;
  @Input() isReadOnly = false;
  @Input() pasteEnabled = false;
  @Input() clearable = true;
  @Input() maxSelectedLabels: number;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage;
  @Input() showClearItem = true;
  @Input() truncateLabels = false;
  @Input() closeOnSelect = true;
  @Input() addTagText: I18nMessage = {messageKey: 'Add item'};
  @Input() addTagValidateFn: any;
  @Input() addTagValidateMessage: I18nMessage = {messageKey: 'Invalid'};
  @Input() size: ACE_SELECT_SIZE = ACE_SELECT_SIZE.MD;
  @Input() sizeDropdownToContent = true;
  @Input() showItemsWhileLoading = false;
  @Input() hideValueLabelsWhileLoading = false;
  @Input() customItemTooltip: ((item: any) => I18nMessage | null) = null;

  @Output() changeEvent: EventEmitter<any> = new EventEmitter();
  @Output() touched: EventEmitter<any> = new EventEmitter();
  @Output() focusEvent: EventEmitter<any> = new EventEmitter();
  @Output() openEvent: EventEmitter<any> = new EventEmitter();
  @Output() closeEvent: EventEmitter<any> = new EventEmitter();
  @Output() clear: EventEmitter<any> = new EventEmitter();
  @Output() scrollToEnd: EventEmitter<any> = new EventEmitter();
  @Output() addEvent: EventEmitter<any> = new EventEmitter();
  @Output() removeEvent: EventEmitter<AceSelectOption> = new EventEmitter();

  @ContentChild('labelTemplate') labelTemplate: TemplateRef<any>;
  @ContentChild('optionTemplate') optionTemplate: TemplateRef<any>;
  @ContentChild('multiLabelTemplate') multiLabelTemplate: TemplateRef<any>;
  @ContentChild('groupOptionTemplate') groupOptionTemplate: TemplateRef<any>;
  @ContentChild('pasteTemplate') pasteTemplate: TemplateRef<any>;

  @ViewChild('ngselect', {static: true})
  ngselect: NgSelectComponent;
  showPasteTemplate = false;
  itemsToAdd: string[] = [];
  readonly ACE_SELECT_SIZE = ACE_SELECT_SIZE;

  private readonly ACE_SELECT_WINDOW_WIDTH_LEEWAY = 30;
  private readonly SCROLL_DEBOUNCE_TIME = 200;
  private readonly CONTENT_WIDTH_CLASS_NAME = 'ace-select-content-width';
  private readonly CONTENT_WIDTH_CSS_VAR_NAME = '--ace-select-content-width-value';
  private readonly logger: AceLogger;
  private readonly scrollSubject$ = new Subject<void>();

  private viewInitialised = false;
  private bufferedWriteValue = {
    buffered: false,
    value: null
  };

  private adjustPositionFn: (() => void) | null;
  private adjustDropdownWidthFn: (() => void) | null;
  private adjustDropdownWidthTimeout: ReturnType<typeof setTimeout>;
  private virtualScrollDropdownMaxOptionWidth: number | null;

  constructor(private renderer: Renderer2,
              private cdRef: ChangeDetectorRef,
              private cssVariablesService: CssVariablesService,
              private htmlUtils: HtmlUtils,
              private translateService: MessageTranslateService,
              logService: AceLoggerService) {
    this.logger = logService.create(AceSelectComponent.name);
  }

  private onChange = (_: any) => {
  };
  private onTouched = () => {
  };

  private onNgSelectOnChange = (selected: any) => {
    this.cdRef.detectChanges(); // required to detect changes in ng-multi-label-tmp
    this.onChange(selected);
    this.changeEvent.emit(selected);
  };

  private onNgSelectOnTouched = () => {
    this.logger.debug('onNgSelectOnTouched()');
    this.onTouched();
    this.touched.emit();
  };

  onNgSelectOnFocus(event) {
    this.logger.debug('AceSelectComponent onNgSelectOnFocus()');
    this.focusEvent.emit(event);
  }

  ngOnInit(): void {
    // ng-select copies value of `classes` to panel component classList when `appendTo` is not empty.
    // https://github.com/ng-select/ng-select/issues/1199#issuecomment-498037651
    if (this.size === ACE_SELECT_SIZE.SM && this.appendTo != null) {
      this.ngselect.classes = (this.ngselect.classes ?? '') + ' ' + 'ng-select-sm';
    }
    this.ngselect.registerOnChange(this.onNgSelectOnChange);
    this.ngselect.registerOnTouched(this.onNgSelectOnTouched);
    if (this.multiSelect && this.selectableGroup == null) {
      this.selectableGroup = true;
    }
    this.scrollSubject$.pipe(debounceTime(this.SCROLL_DEBOUNCE_TIME)).subscribe(() => this.adjustDropdownWidthBasedOnContent());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      let resetVirtualScrollDropdownWidth = false;

      if (changes.sizeDropdownToContent) {
        resetVirtualScrollDropdownWidth = true;
        if (this.ngselect?.isOpen) {
          this.adjustDropdownWidthFn = this.sizeDropdownToContent ? this.initDropdownWidthBasedOnContent : this.removeDropdownWidthBasedOnContent;
        }
      }

      if (changes.loading) {
        resetVirtualScrollDropdownWidth = true;
        if (this.ngselect?.isOpen && this.sizeDropdownToContent && this.virtualScroll) {
          this.scheduleAdjustDropdownWidthBasedOnContent();
        }
      }

      if (changes.items) {
        resetVirtualScrollDropdownWidth = true;
      }

      if (resetVirtualScrollDropdownWidth) {
        this.virtualScrollDropdownMaxOptionWidth = null;
      }
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialised = true;
    if (this.bufferedWriteValue && this.bufferedWriteValue.buffered) {
      this.writeValue(this.bufferedWriteValue.value);
      this.bufferedWriteValue = {
        buffered: false,
        value: null
      };
      this.cdRef.detectChanges();
    }
  }

  ngAfterViewChecked(): void {
    this.adjustPositionFn?.();
    this.adjustPositionFn = null;
    this.adjustDropdownWidthFn?.();
    this.adjustDropdownWidthFn = null;
  }

  ngOnDestroy(): void {
    this.clearAdjustDropdownWidthTimeout();
    this.scrollSubject$.complete();
  }

  writeValue(obj: any): void {
    if (this.viewInitialised) {
      this.ngselect.writeValue(obj);
    } else {
      this.bufferedWriteValue = {
        buffered: true,
        value: obj
      };
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.ngselect.setDisabledState(isDisabled);
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData || window['clipboardData'];
    const pastedText = clipboardData.getData('text');
    if (pastedText && pastedText.length) {
      this.itemsToAdd = [];
      const itemsToSelect: string[] = [];
      const pastedItems: string[] = this.multiSelect ? CsvUtils.parseAsSingleLine(pastedText) : [pastedText];

      pastedItems.forEach((item: string) => {
        if (this.isExistingItem(item)) {
          itemsToSelect.push(item);
        } else if (item) {
          this.itemsToAdd.push(item);
        }
      });

      itemsToSelect.forEach(item => {
        const foundItem = this.ngselect.itemsList.findItem(item);
        this.ngselect.select(foundItem);
      });

      if (this.itemsToAdd && this.itemsToAdd.length) {
        this.showPasteTemplate = true;
        this.items = [...this.items];
      }

      // reopen select since selecting items closes it (if closeOnSelect which defaults to true)
      if (itemsToSelect && itemsToSelect.length && this.ngselect.closeOnSelect) {
        this.ngselect.open();
      }
    }
  }

  private isExistingItem(itemLabel: string): boolean {
    return this.items.find((item: any) => {
      if (this.bindLabel) {
        return item[this.bindLabel] === itemLabel;
      } else {
        return item === itemLabel;
      }
    });
  }

  onOpen() {
    if (this.sizeDropdownToContent) {
      this.adjustDropdownWidthFn = this.initDropdownWidthBasedOnContent;
    }

    // open the ng-select dropdown panel to the left when panel goes offscreen
    this.adjustPositionFn = () => {
      const selectBoundingRect = this.ngselect.element.getBoundingClientRect();
      const documentWidth = document.body.clientWidth;
      const ngDropdownPanelElement: HTMLElement = document.querySelector('.ng-dropdown-panel');
      if (ngDropdownPanelElement) {
        const ngDropdownPanelBoundingRect = ngDropdownPanelElement.getBoundingClientRect();
        if (ngDropdownPanelBoundingRect.right > documentWidth && (selectBoundingRect.right - ngDropdownPanelBoundingRect.width) > 0) {
          ngDropdownPanelElement.style.left = 'unset';
          const offsetRight = this.appendTo != null
            ? documentWidth - selectBoundingRect.right
            : 0;
          ngDropdownPanelElement.style.right = offsetRight + 'px';
        }
      } else {
        this.logger.error('ng-select dropdown panel could not be found');
      }
    }
    this.openEvent.emit();
  }

  onClose() {
    this.clearAdjustDropdownWidthTimeout();
    this.adjustDropdownWidthFn = null;
    this.showPasteTemplate = false;
    this.closeEvent.emit();
  }

  onClear() {
    this.clear.emit();
  }

  onScrollToEnd() {
    this.scrollToEnd.emit();
  }

  onScroll() {
    if (this.sizeDropdownToContent && this.virtualScroll) {
      this.scrollSubject$.next();
    }
  }

  isAddedTagValid(searchTerm: string): boolean {
    return this.addTagValidateFn != null ? this.addTagValidateFn(searchTerm) : true;
  }

  addItem(searchTerm: string) {
    this.itemsToAdd = Array.of(searchTerm);
    this.addItems();
  }

  addItems() {
    // apply add function to new items if exists
    let newItems: any[];
    if (this.addTag && typeof (this.addTag) === 'function') {
      newItems = this.itemsToAdd.map(item => (this.addTag as AddTagFn)(item));
    } else {
      newItems = this.itemsToAdd;
    }
    this.itemsToAdd = [];
    this.items = [...newItems, ...this.items];
    this.selectItems([...newItems, ...this.ngselect.selectedValues]);
  }

  open() {
    this.ngselect.open();
  }

  focus(): void {
    this.ngselect.focus();
  }

  onAdd(item: any): void {
    this.addEvent.emit(item);
  }

  onRemove(item: AceSelectOption): void {
    this.removeEvent.emit(item);
  }

  private selectItems(itemsToSelect: any[]) {
    // need timeout or else NgSelect hasn't updated the list of items so find fails
    setTimeout(() => {
      itemsToSelect.forEach(item => {
        const foundItem = this.ngselect.itemsList.findItem(item);
        if (foundItem) {
          this.ngselect.select(foundItem);
        } else {
          throw new Error('Error selecting new item: ' + JSON.stringify(item));
        }
      });
      this.ngselect.close();
    });
  }

  get showAddTag() {
    const term = this.ngselect.searchTerm && this.ngselect.searchTerm.toLowerCase().trim();
    return this.addTag && this.ngselect.itemsList && this.ngselect.itemsList.filteredItems &&
      (!this.ngselect.itemsList.filteredItems.some(x => x.label.toLowerCase() === term) &&
        (!this.ngselect.hideSelected && this.ngselect.isOpen || !this.ngselect.selectedItems.some(x => x.label.toLowerCase() === term))) && !this.ngselect.loading;
  }

  private getDropdownElement(): HTMLElement {
    if (this.ngselect) {
      const dropdownElement = document.getElementById(this.ngselect.dropdownId);
      if (dropdownElement) {
        return dropdownElement;
      } else {
        this.logger.debug('Cannot find dropdown as cannot find dropdown with id', this.ngselect.dropdownId);
      }
    } else {
      this.logger.debug('Cannot find dropdown as ng-select is not initialised');
    }
    return null;
  }

  private initDropdownWidthBasedOnContent(): void {
    const dropdownElement = this.getDropdownElement();
    if (dropdownElement) {
      if (this.virtualScroll) {
        this.scheduleAdjustDropdownWidthBasedOnContent();
      } else {
        this.cssVariablesService.setProperty(dropdownElement, this.CONTENT_WIDTH_CSS_VAR_NAME, 'auto');
      }
      this.renderer.addClass(dropdownElement, this.CONTENT_WIDTH_CLASS_NAME);
    } else {
      this.logger.warn('Cannot apply automatically adjusted width based on content to dropdown as it could not be found');
    }
  }

  private removeDropdownWidthBasedOnContent(): void {
    const dropdownElement = this.getDropdownElement();
    if (dropdownElement) {
      this.renderer.removeClass(dropdownElement, this.CONTENT_WIDTH_CLASS_NAME);
      this.cssVariablesService.setProperty(dropdownElement, this.CONTENT_WIDTH_CSS_VAR_NAME, null);
    } else {
      this.logger.warn('Cannot remove automatically adjusted width based on content from dropdown as it could not be found');
    }
  }

  private adjustDropdownWidthBasedOnContent(): void {
    const dropdownElement = this.getDropdownElement();
    const scrollBarWidth = this.htmlUtils.getScrollBarWidth();
    if (dropdownElement) {
      // Use .ng-option, and not e.g. ng-option-child, to ensure that the Loading item is picked up (when items are being loaded)
      const options = Array.from(dropdownElement.querySelectorAll<HTMLElement>('.ng-option'));
      const groups = Array.from(dropdownElement.querySelectorAll<HTMLElement>('.ng-optgroup'));
      try {
        let maxOptionWidth = options.concat(groups).reduce((maxWidth, option) => {
          return Math.max(maxWidth, option.getBoundingClientRect().width);
        }, 0);

        if (maxOptionWidth > 0) {
          // Option width includes paddings and scroll bar width
          maxOptionWidth += scrollBarWidth;
          if (this.virtualScrollDropdownMaxOptionWidth == null || maxOptionWidth > this.virtualScrollDropdownMaxOptionWidth) {
            this.virtualScrollDropdownMaxOptionWidth = maxOptionWidth;
          }

          // Ensure the list width is not wider than the screen width
          const dropdownWidth = Math.min(this.virtualScrollDropdownMaxOptionWidth ?? 0, window.innerWidth - this.ACE_SELECT_WINDOW_WIDTH_LEEWAY * 2);
          const currentDropdownWith = dropdownElement.getBoundingClientRect().width;
          if (dropdownWidth != currentDropdownWith) {
            this.cssVariablesService.setProperty(dropdownElement, this.CONTENT_WIDTH_CSS_VAR_NAME, dropdownWidth + 'px');
          }
        } else {
          this.logger.warn('Could not determine the width of contents of dropdown');
        }
      } catch (e) {
        this.logger.error('Cannot recalculate width for dropdown', e);
      }
    } else {
      this.logger.warn('Cannot recalculate dropdown width based on content as it could not be found');
    }
  }

  private clearAdjustDropdownWidthTimeout(): void {
    if (this.adjustDropdownWidthTimeout != null) {
      clearTimeout(this.adjustDropdownWidthTimeout);
    }
    this.adjustDropdownWidthTimeout = null;
  }

  private scheduleAdjustDropdownWidthBasedOnContent(): void {
    this.clearAdjustDropdownWidthTimeout();
    // Need to wait until the dropdown is populated
    this.adjustDropdownWidthTimeout = setTimeout(() => {
      this.adjustDropdownWidthTimeout = null;
      this.adjustDropdownWidthBasedOnContent();
    }, 0);
  }

  getToolTip(item: NgOption): string | null {
    if (this.customItemTooltip != null) {
      const customTooltip = this.customItemTooltip(item?.value);
      return customTooltip ? this.translateService.instant(customTooltip) : null;
    } else {
      return item?.label;
    }
  }

  isOpen(): boolean {
    return this.ngselect.isOpen;
  }

}
